import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'components/core/Link';
import { H3, H4 } from 'components/core/Typography';
import { Image } from 'components/core/Image';
import { UnitIcon } from 'components/unit/UnitIcon';
import { Text } from 'components/core/Typography/Typography';
import YellowArrow from 'assets/images/icons/yellow-arrow.svg';
import greenPriceTag from 'assets/images/icons/green-price-tag.svg';

import './BestPricedUnits.scss';
import { formatMonthDayYear } from '../AverageUnitPrice/utils';

/**
 * @param {Object} props - The component's props
 * @param {Object} props.location - The search location object
 * @param {Object[]} props.units - An array of units
 * @param {Object[]} props.refreshDate - Last date in data (last time refresh job was run)
 */
export function BestPricedUnits({ location, units, refreshDate, vehicleType }) {
	if (!units || !units.length) return null;

	return (
		<div className="best-priced-units">
			<div>
				<H3 size="medium">
					Cheapest Storage in {location.city}, {location.state}
				</H3>

				{!!units?.length && (
					<ul className="best-unit-list">
						{units.map((unit) => {
							const [integerPart, decimalPart] =
								unit?.price?.toString()?.indexOf('.') !== -1
									? unit?.price?.toString()?.split('.')
									: [unit?.price?.toString(), '00'];

							return (
								<li
									key={unit.unit}
									className="unit-card"
								>
									<div className="card-header">
										<div className="unit-size-row">
											<UnitIcon
												unitSize={
													/^(\d+)'$/.test(unit.sizeText)
														? `10x${unit.sizeText.replace("'", '')}-${vehicleType === 'boat' ? 'boat' : 'parking_space'}`
														: `${unit.sizeText}-storage`
												}
												alt={'sizeIcon'}
												lazyLoad={true}
											/>

											<H4 className="sf-type-medium">
												Cheapest {unit?.sizeText} storage in {location.city}, {location.state}
											</H4>
										</div>

										<div className="price-arrow-container">
											<div className="top-right-text">
												<Text>
													<Text
														className="sf-type-semiLarge"
														weight="ultraBold"
													>
														${integerPart}
													</Text>

													<Text className="sf-type-small cents-text">
														.{decimalPart}
													</Text>
												</Text>

												<Text
													className="sf-type-small sf-type-weight-light"
													color="gray"
													strikethrough
												>
													${unit.averagePrice}
												</Text>
											</div>
											<Link
												href={unit.reserveUrl}
												className="arrow-link"
												hideBorder
											>
												<Image
													src={YellowArrow}
													className="right-arrow"
													alt={'DetailsArrow'}
												/>
											</Link>
										</div>
									</div>

									<div className="card-footer">
										<Text
											className="sf-type-tiny average-text"
											color="green"
											weight="ultraBold"
										>
											<Image
												src={greenPriceTag}
												alt={'priceTag'}
												height={16}
												width={16}
											/>
											$
											{(
												unit.averagePrice - unit.price
											)?.toFixed(2)}{' '}
											Below average unit price
										</Text>

										<Text
											className="sf-type-tiny facility-name"
											weight="ultraBold"
										>
											{unit.facilityName}
										</Text>
									</div>
								</li>
							);
						})}
					</ul>
				)}

				<Text
					className="sf-type-tiny"
					weight="standard"
					color="gray"
				>
					Average prices last updated on {refreshDate}.
				</Text>
			</div>
		</div>
	);
}

BestPricedUnits.propTypes = {
	location: PropTypes.shape({
		city: PropTypes.string.isRequired,
		state: PropTypes.string.isRequired,
	}).isRequired,
	units: PropTypes.arrayOf(
		PropTypes.shape({
			averagePrice: PropTypes.number,
			facility: PropTypes.number,
			facilityCity: PropTypes.string,
			facilityImage: PropTypes.string,
			facilityName: PropTypes.string,
			facilityState: PropTypes.string,
			inNetwork: PropTypes.bool,
			price: PropTypes.string,
			reserveUrl: PropTypes.string,
			sizeText: PropTypes.string,
			unit: PropTypes.number,
			squareFootage: PropTypes.number,
		})
	),
	pricingStats: PropTypes.shape({
		lowestPrices: PropTypes.object,
		averagePrices: PropTypes.object,
	}),
	refreshDate: PropTypes.string,
	vehicleType: PropTypes.string,
};

BestPricedUnits.defaultProps = {
	units: [],
	refreshDate: formatMonthDayYear(new Date().toISOString().split('T')[0]),
};

export default BestPricedUnits;
